package com.iregained.site.components.sections.home.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.home.styles.SecondaryButtonStyle
import com.iregained.site.components.sections.home.styles.TestimonialPlayButtonStyle
import com.iregained.site.components.sections.home.styles.TestimonialThumbnailStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Iframe

@Composable
private fun Modifier.deepSweepUp(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 80.px).transition(
        Transition.of("opacity", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.8.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun HomeVideoTestimonialsSection(data: HomeVideoTestimonialsSectionData = defaultHomeVideoTestimonialsSectionData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "home-video-testimonials"

    var activeVideoUrl by remember { mutableStateOf<String?>(null) }
    var isVisible by remember { mutableStateOf(false) }

    if (data.videos.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element)
                            onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {

                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 40.px).deepSweepUp(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    SimpleGrid(
                        numColumns = numColumns(base = 1, md = 2, lg = 3),
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(40.px)
                    ) {
                        data.videos.take(3).forEachIndexed { index, videoData ->
                            key(videoData.videoUrl) {
                                TestimonialVideo(
                                    data = videoData,
                                    onClick = { activeVideoUrl = videoData.videoUrl },
                                    modifier = Modifier.deepSweepUp(isVisible, 200 + (index * 200))
                                )
                            }
                        }
                    }

                    if (data.ctaLinkPath.isNotBlank() && data.ctaButtonText.isNotBlank()) {
                        Link(
                            path = data.ctaLinkPath,
                            modifier = Modifier.textDecorationLine(TextDecorationLine.None).margin(top = 60.px)
                                .deepSweepUp(isVisible, 800)
                        ) {
                            Box(
                                modifier = SecondaryButtonStyle.toModifier()
                                    .padding(leftRight = 32.px, topBottom = 16.px).borderRadius(8.px)
                                    .cursor(Cursor.Pointer), contentAlignment = Alignment.Center
                            ) {
                                SpanText(
                                    text = data.ctaButtonText,
                                    modifier = Modifier.fontSize(18.px).fontWeight(FontWeight.SemiBold)
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    if (activeVideoUrl != null) {
        VideoModal(videoUrl = activeVideoUrl!!, onClose = { activeVideoUrl = null })
    }
}

@Composable
private fun TestimonialVideo(data: TestimonialData, onClick: () -> Unit, modifier: Modifier = Modifier) {
    val palette = ColorMode.current.toSitePalette()
    Column(modifier = modifier.fillMaxWidth()) {
        Box(
            modifier = Modifier.fillMaxWidth().styleModifier { property("aspect-ratio", "16 / 9") }.borderRadius(12.px)
                .overflow(Overflow.Hidden)
                .boxShadow(0.px, 10.px, 30.px, color = palette.custom.darkText.toRgb().copyf(alpha = 0.1f))
                .cursor(Cursor.Pointer).onClick { onClick() }, contentAlignment = Alignment.Center
        ) {
            if (data.thumbnailUrl.isNotBlank()) Image(
                src = data.thumbnailUrl,
                modifier = TestimonialThumbnailStyle.toModifier().unextractable().fillMaxSize().objectFit(ObjectFit.Cover)
            )
            Box(
                modifier = TestimonialPlayButtonStyle.toModifier().size(60.px).borderRadius(50.percent),
                contentAlignment = Alignment.Center
            ) { SpanText("▶", modifier = Modifier.color(Colors.White).fontSize(24.px).margin(left = 4.px)) }
        }
        Column(modifier = Modifier.padding(top = 20.px, leftRight = 4.px)) {
            SpanText(
                text = data.name,
                modifier = Modifier.color(palette.custom.darkText).fontWeight(FontWeight.Bold).fontSize(20.px)
                    .margin(bottom = 4.px)
            )
            SpanText(
                text = data.role,
                modifier = Modifier.color(palette.custom.darkText).opacity(0.6f).fontStyle(FontStyle.Italic)
                    .fontSize(15.px).margin(bottom = 16.px)
            )
            SpanText(
                text = "\"${data.quote}\"",
                modifier = Modifier.color(palette.brand.primary).fontWeight(FontWeight.SemiBold).fontSize(18.px)
                    .lineHeight(1.5)
            )
        }
    }
}

@Composable
private fun VideoModal(videoUrl: String, onClose: () -> Unit) {
    val autoplayUrl = if (videoUrl.contains("?")) "$videoUrl&autoplay=1" else "$videoUrl?autoplay=1"
    Box(
        modifier = Modifier.fillMaxSize().position(Position.Fixed).top(0.px).left(0.px).zIndex(9999)
            .backgroundColor(rgba(0, 0, 0, 0.85)).onClick { onClose() }, contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier.position(Position.Absolute).top(30.px).right(30.px).size(40.px).cursor(Cursor.Pointer)
                .onClick { onClose() }, contentAlignment = Alignment.Center
        ) { SpanText("✕", modifier = Modifier.color(Colors.White).fontSize(30.px)) }
        Box(
            modifier = Modifier.fillMaxWidth(90.percent).maxWidth(1000.px)
            .styleModifier { property("aspect-ratio", "16 / 9") }.backgroundColor(Colors.Black)
            .boxShadow(0.px, 20.px, 50.px, color = rgba(0, 0, 0, 0.5)).onClick { it.stopPropagation() }) {
            val isSafeUrl =
                autoplayUrl.contains("youtube.com/embed/") || autoplayUrl.contains("player.vimeo.com/video/")
            if (isSafeUrl) {
                Iframe(
                    attrs = Modifier.fillMaxSize().border(0.px, LineStyle.None, Colors.Transparent).toAttrs {
                        attr("src", autoplayUrl); attr(
                        "allow",
                        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    ); attr("allowfullscreen", "true")
                    })
            } else {
                Box(
                    modifier = Modifier.fillMaxSize().backgroundColor(Colors.Black), contentAlignment = Alignment.Center
                ) { SpanText("Video unavailable.", modifier = Modifier.color(Colors.White)) }
            }
        }
    }
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field thumbnailUrl:
 * - Aspect Ratio: 16:9 (Standard Video format).
 * - Recommended Dimensions: 1280x720px.
 * - Max File Size: 400KB (WebP or JPEG).
 * - Behavior: Enforced 16/9 aspect-ratio in UI; ObjectFit.Cover applied.
 */
data class TestimonialData(
    val name: String, val role: String, val quote: String, val thumbnailUrl: String, val videoUrl: String
)

data class HomeVideoTestimonialsSectionData(
    val sectionId: String? = null,
    val sectionHeader: String,
    val ctaButtonText: String,
    val ctaLinkPath: String,
    val videos: List<TestimonialData>
)

val defaultHomeVideoTestimonialsSectionData = HomeVideoTestimonialsSectionData(
    sectionId = "home-video-testimonials",
    sectionHeader = "OUR IMPACT THROUGH PATIENT STORIES",
    ctaButtonText = "Watch More Success Stories →",
    ctaLinkPath = "/testimonials",
    videos = listOf(
        TestimonialData(
            "Beverly Charles",
            "IRegained Patient Volunteer",
            "Hand movements after four years of stroke.",
            Res.Images.Home.Testimonials.THUMB_BEVERLY,
            Res.Strings.Links.TESTIMONIAL_BEVERLEY
        ), TestimonialData(
            "David Leblanc",
            "IRegained Patient Volunteer",
            "I can use a nail clipper.",
            Res.Images.Home.Testimonials.THUMB_DAVID,
            Res.Strings.Links.TESTIMONIAL_DAVID
        ), TestimonialData(
            "Dr. Byron Eastman",
            "IRegained Patient Volunteer",
            "Picked up photography again after a long hiatus.",
            Res.Images.Home.Testimonials.THUMB_BYRON,
            Res.Strings.Links.TESTIMONIAL_BYRON
        )
    )
)